import React from 'react';

const steps = [
  { num: '01', title: 'Diagnose', desc: 'We assess your brand, marketing systems, operations, and competitive position — identifying structural gaps before recommending a single execution move.' },
  { num: '02', title: 'Architect', desc: 'We design the full system — positioning, content pillars, paid structure, funnel architecture, and backend workflows — as a coherent, integrated plan.' },
  { num: '03', title: 'Build', desc: 'We build the systems: brand assets, content infrastructure, ad campaigns, website, email flows, and operational frameworks — all connected by design.' },
  { num: '04', title: 'Execute', desc: 'We run the systems — managing content, ads, campaigns, and communications — with consistent quality, strategic intent, and clear KPIs at every stage.' },
  { num: '05', title: 'Compound', desc: "We review, refine, and scale. What's working gets amplified. What isn't gets fixed. The system becomes self-reinforcing and growth becomes consistent." },
];

const StepCard: React.FC<typeof steps[0]> = ({ num, title, desc }) => (
  <div
    className="fade-up flex flex-col items-center text-center"
    style={{ padding: '0 32px 48px' }}
  >
    <div
      style={{
        width: 64, height: 64,
        border: '1px solid rgba(184,154,90,0.22)',
        borderRadius: '50%',
        background: '#080807',
        boxShadow: '0 0 28px rgba(184,154,90,0.05)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 28, flexShrink: 0,
        position: 'relative', zIndex: 1,
      }}
    >
      <span style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 22, fontWeight: 300, color: '#b89a5a' }}>{num}</span>
    </div>
    <h3 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 20, fontWeight: 400, color: '#f2ece0', marginBottom: 14 }}>{title}</h3>
    <p style={{ fontSize: 12, fontWeight: 300, color: '#78736c', lineHeight: 2, letterSpacing: '0.02em', maxWidth: 260 }}>{desc}</p>
  </div>
);

const Method: React.FC = () => (
  <section
    id="method"
    className="relative py-[120px] px-10"
    style={{ background: 'linear-gradient(195deg, #080807 0%, #0d0c0b 100%)' }}
  >
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(184,154,90,0.02) 0%, transparent 70%)' }}
    />

    <div className="max-w-[1200px] mx-auto relative z-10">

      {/* Header */}
      <div className="text-center mb-20">
        <div className="flex items-center justify-center gap-[14px] mb-5" style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#b89a5a', fontFamily: 'Montserrat, sans-serif' }}>
          <span style={{ width: 28, height: 1, background: '#b89a5a', display: 'inline-block' }} />
          The HNL Method
          <span style={{ width: 28, height: 1, background: '#b89a5a', display: 'inline-block' }} />
        </div>
        <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 'clamp(34px, 4.5vw, 58px)', fontWeight: 300, lineHeight: 1.1, color: '#f2ece0' }}>
          How We Build.
          <br />
          <em style={{ fontStyle: 'italic', color: '#b89a5a' }}>Why It Works.</em>
        </h2>
        <p style={{ fontSize: 13, fontWeight: 300, color: '#aea89f', maxWidth: 520, margin: '16px auto 0', lineHeight: 2, letterSpacing: '0.02em' }}>
          A structured five-stage process that replaces guesswork with a repeatable, compound growth architecture.
        </p>
        <div style={{ width: 40, height: 1, background: '#b89a5a', margin: '32px auto 0' }} />
      </div>

      {/* ── Row 1: steps 1–3 ── */}
      <div style={{ position: 'relative' }}>
        {/* Connector line behind the circles */}
        <div
          className="hidden md:block absolute pointer-events-none"
          style={{
            top: 32,
            left: 'calc(16.667% + 32px)',
            right: 'calc(16.667% + 32px)',
            height: 1,
            background: 'linear-gradient(to right, transparent, rgba(184,154,90,0.22) 20%, rgba(184,154,90,0.22) 80%, transparent)',
          }}
        />
        <div className="grid grid-cols-1 md:grid-cols-3">
          {steps.slice(0, 3).map((s) => <StepCard key={s.num} {...s} />)}
        </div>
      </div>

      {/* ── Row 2: steps 4–5 centred in a 4-col grid ── */}
      {/* On md: columns 2 & 3 of a 4-col grid → equal width, centred */}
      {/* On sm: single column, same as above                          */}
      <div
        className="grid grid-cols-1 md:grid-cols-4"
        style={{ position: 'relative' }}
      >
        {/* Connector line */}
        <div
          className="hidden md:block absolute pointer-events-none"
          style={{
            top: 32,
            left: 'calc(25% + 32px)',
            right: 'calc(25% + 32px)',
            height: 1,
            background: 'linear-gradient(to right, transparent, rgba(184,154,90,0.22) 20%, rgba(184,154,90,0.22) 80%, transparent)',
          }}
        />
        {/* Empty col for centering */}
        <div className="hidden md:block" />
        {steps.slice(3).map((s) => <StepCard key={s.num} {...s} />)}
        {/* Empty col for centering */}
        <div className="hidden md:block" />
      </div>

    </div>
  </section>
);

export default Method;